/**
 * @file EkosystemDetail.tsx
 * @description Detail of one App/Project entity (GET /api/ecosystem/:id). Shows live status,
 * VPS bindings (vhost/container/pm2), the full Trakcja profile (goal / sprint / next-step /
 * momentum + items grouped by kind + data gaps) when linked, and memory references. The
 * "Wątki Claude Code" section is a Sprint-2 placeholder (transcript scan). Polish copy.
 */

import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Ban,
  CheckCircle2,
  ExternalLink,
  HelpCircle,
  Target,
  XCircle,
  AlertTriangle,
  MessagesSquare,
  FileText,
  Plus,
  Lightbulb,
  Loader2,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { api } from "../lib/api";
import type { EcosystemDetail as Detail, EcoStatus, TrakcjaItemKind } from "../lib/api";

const STATUS: Record<EcoStatus, { cls: string; label: string }> = {
  live: { cls: "text-emerald-400 border-emerald-500/30", label: "🟢 live" },
  stopped: { cls: "text-red-400 border-red-500/30", label: "🔴 zatrzymane" },
  unknown: { cls: "text-gray-400 border-border", label: "— brak odczytu" },
};

const KIND_META: Record<TrakcjaItemKind, { icon: LucideIcon; label: string; cls: string }> = {
  GOAL: { icon: Target, label: "Cele", cls: "text-accent" },
  ASSUMPTION: { icon: HelpCircle, label: "Założenia", cls: "text-gray-400" },
  WIN: { icon: CheckCircle2, label: "Co się udało", cls: "text-emerald-400" },
  LOSS: { icon: XCircle, label: "Co nie wyszło", cls: "text-red-400" },
  BLOCKER: { icon: Ban, label: "Blokery", cls: "text-amber-400" },
};
const KIND_ORDER: TrakcjaItemKind[] = ["BLOCKER", "GOAL", "ASSUMPTION", "WIN", "LOSS"];
const known = (v: string | null | undefined) =>
  Boolean(v && v.trim() && v.trim().toLowerCase() !== "brak danych");

const CONF: Record<string, string> = {
  high: "text-emerald-400 border-emerald-500/30",
  med: "text-amber-400 border-amber-500/30",
  low: "text-gray-500 border-border",
};
const CONF_LABEL: Record<string, string> = { high: "pewne", med: "śr.", low: "słabe" };

export function EkosystemDetail() {
  const { id } = useParams<{ id: string }>();
  const [d, setD] = useState<Detail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [addedKeys, setAddedKeys] = useState<Set<string>>(new Set());
  const [busyKey, setBusyKey] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      setD(await api.ecosystem.getOne(id));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const p = d?.profile;

  // Sugestie „co dalej" = następny krok + otwarte blockery z profilu Trakcji.
  const suggestions: { key: string; text: string }[] = [];
  if (p) {
    if (known(p.next_step)) suggestions.push({ key: "next", text: p.next_step as string });
    p.items.forEach((it, i) => {
      if (it.kind === "BLOCKER" && it.status !== "DONE")
        suggestions.push({ key: `b${i}`, text: it.text });
    });
  }

  const addToHermes = async (text: string, key: string) => {
    if (!d) return;
    setBusyKey(key);
    try {
      await api.tasks.create({
        title: text,
        notes: `Z Ekosystemu: ${d.name}`,
        priority: d.status === "stopped" ? "HIGH" : "MEDIUM",
      });
      setAddedKeys((s) => new Set(s).add(key));
    } catch {
      /* przycisk po prostu nie przeskoczy na „W Hermesie" */
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <div className="animate-fade-in space-y-6 max-w-3xl">
      <Link
        to="/ekosystem"
        className="inline-flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-200 no-underline"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> Ekosystem
      </Link>

      {loading && !d && <div className="animate-pulse h-24 bg-surface-2 rounded-xl" />}
      {error && !d && (
        <div className="card p-4 flex items-center gap-3 border-red-500/30">
          <AlertTriangle className="w-5 h-5 text-red-400" />
          <p className="text-xs text-gray-500 font-mono truncate">{error}</p>
        </div>
      )}

      {d && (
        <>
          {/* Header */}
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-lg font-semibold text-gray-100">{d.name}</h1>
            <span
              className={`text-xs font-medium px-2 py-0.5 rounded border ${STATUS[d.status].cls}`}
            >
              {STATUS[d.status].label}
            </span>
            <span className="text-[10px] text-gray-500 border border-border rounded px-1.5 py-0.5">
              {d.kind}
            </span>
          </div>
          {d.statusDetail && (
            <p className="text-[11px] font-mono text-gray-600 -mt-3">{d.statusDetail}</p>
          )}

          {/* VPS bindings */}
          {d.vps && (
            <div className="card p-3 space-y-1.5">
              <p className="text-xs font-semibold text-gray-300">Na serwerze</p>
              {d.vps.vhost && (
                <a
                  href={`https://${d.vps.vhost}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs text-accent no-underline hover:underline"
                >
                  {d.vps.vhost} <ExternalLink className="w-3 h-3" />
                </a>
              )}
              <div className="flex flex-wrap gap-x-4 text-[11px] text-gray-500">
                {d.vps.container && (
                  <span>
                    docker: <span className="font-mono text-gray-300">{d.vps.container}</span>
                  </span>
                )}
                {d.vps.pm2 && (
                  <span>
                    pm2: <span className="font-mono text-gray-300">{d.vps.pm2}</span>
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Trakcja profile */}
          {d.profileMissing && (
            <div className="card p-3 border-amber-500/20 bg-amber-500/5 text-xs text-amber-300">
              Brak wpisu w Trakcji — dodaj projekt, żeby zobaczyć cel/etap/zadania tutaj.
            </div>
          )}
          {p && (
            <div className="card p-4 space-y-3">
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-gray-500">
                {known(p.stage) && (
                  <span>
                    Etap: <span className="text-gray-300">{p.stage}</span>
                  </span>
                )}
                {known(p.current_sprint) && (
                  <span>
                    Sprint: <span className="text-gray-300">{p.current_sprint}</span>
                  </span>
                )}
                {known(p.deadline_at) && (
                  <span>
                    Termin: <span className="text-gray-300">{p.deadline_at}</span>
                  </span>
                )}
                {known(p.money_note) && (
                  <span>
                    Kasa: <span className="text-gray-300">{p.money_note}</span>
                  </span>
                )}
              </div>
              {known(p.current_goal) && (
                <p className="text-sm text-gray-200">
                  <span className="text-gray-500">Cel: </span>
                  {p.current_goal}
                </p>
              )}
              {known(p.next_step) && (
                <p className="text-sm text-gray-200 rounded-lg bg-surface-2 border border-border p-2.5">
                  <span className="text-accent text-xs">▶ Następny krok: </span>
                  {p.next_step}
                </p>
              )}
              {known(p.momentum_reason) && (
                <p className="text-xs text-gray-400 italic">{p.momentum_reason}</p>
              )}

              {KIND_ORDER.map((kind) => {
                const items = p.items.filter((i) => i.kind === kind);
                if (items.length === 0) return null;
                const meta = KIND_META[kind];
                const Icon = meta.icon;
                return (
                  <div key={kind}>
                    <p className="flex items-center gap-1.5 text-xs font-medium text-gray-300 mt-2 mb-1">
                      <Icon className={`w-3.5 h-3.5 ${meta.cls}`} /> {meta.label}
                      <span className="text-gray-500">{items.length}</span>
                    </p>
                    <ul className="space-y-1 pl-5">
                      {items.map((it, idx) => (
                        <li key={idx} className="text-xs text-gray-400 list-disc">
                          {it.text}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}

              {p.data_gaps.length > 0 && (
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {p.data_gaps.map((g, i) => (
                    <span
                      key={i}
                      className="text-[10px] text-amber-400/80 bg-amber-500/5 border border-amber-500/20 rounded px-1.5 py-0.5"
                      title={g}
                    >
                      brak danych: {g.length > 40 ? `${g.slice(0, 40)}…` : g}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Sugestie — co dalej (akcjonowalne → Hermes) */}
          {suggestions.length > 0 && (
            <section>
              <div className="flex items-center gap-2 mb-2">
                <Lightbulb className="w-4 h-4 text-accent" />
                <h2 className="text-sm font-semibold text-gray-100">Sugestie — co dalej</h2>
              </div>
              <div className="space-y-1.5">
                {suggestions.map((sg) => {
                  const added = addedKeys.has(sg.key);
                  return (
                    <div key={sg.key} className="card p-2.5 flex items-start gap-2">
                      <span className="text-xs text-gray-300 flex-1">{sg.text}</span>
                      <button
                        type="button"
                        onClick={() => addToHermes(sg.text, sg.key)}
                        disabled={added || busyKey === sg.key}
                        className={`flex-shrink-0 text-[10px] font-medium inline-flex items-center gap-1 px-1.5 py-0.5 rounded border transition-colors disabled:opacity-70 ${
                          added
                            ? "text-emerald-400 border-emerald-500/30"
                            : "text-gray-400 border-border hover:text-gray-200 hover:bg-surface-3"
                        }`}
                      >
                        {busyKey === sg.key ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : added ? (
                          <CheckCircle2 className="w-3 h-3" />
                        ) : (
                          <Plus className="w-3 h-3" />
                        )}
                        {added ? "W Hermesie" : "Do Hermesa"}
                      </button>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {/* Memory refs */}
          {d.memoryFiles && d.memoryFiles.length > 0 && (
            <div className="card p-3">
              <p className="text-xs font-semibold text-gray-300 mb-2">Powiązane memory</p>
              <div className="flex flex-wrap gap-1.5">
                {d.memoryFiles.map((m) => (
                  <span
                    key={m}
                    className="inline-flex items-center gap-1 text-[11px] text-gray-400 font-mono border border-border rounded px-1.5 py-0.5"
                  >
                    <FileText className="w-3 h-3" /> {m}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Wątki CC — co robiły nad apką (skan transkryptów) */}
          <section>
            <div className="flex items-center gap-2 mb-2">
              <MessagesSquare className="w-4 h-4 text-accent" />
              <h2 className="text-sm font-semibold text-gray-100">Wątki Claude Code</h2>
              <span className="text-[11px] text-gray-500">{d.threads.length}</span>
            </div>
            {d.threads.length === 0 ? (
              <p className="text-xs text-gray-500 italic">
                Brak dopasowanych wątków (auto-mapowanie po transkryptach — odśwież mapper).
              </p>
            ) : (
              <div className="space-y-1.5">
                {d.threads.map((t) => (
                  <div key={t.sessionId} className="card p-2.5">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-gray-200 truncate flex-1">
                        {t.title}
                      </span>
                      <span
                        className={`text-[9px] px-1 py-0.5 rounded border flex-shrink-0 ${CONF[t.confidence]}`}
                      >
                        {CONF_LABEL[t.confidence]}
                      </span>
                      {t.date && (
                        <span className="text-[10px] text-gray-600 font-mono flex-shrink-0">
                          {t.date.slice(0, 10)}
                        </span>
                      )}
                    </div>
                    {t.summary && <p className="text-[11px] text-gray-500 mt-1">{t.summary}</p>}
                    <div className="flex items-center gap-3 mt-1 text-[10px] text-gray-600">
                      {t.edits > 0 && <span>{t.edits} edycji</span>}
                      <Link
                        to={`/sessions/${t.sessionId}`}
                        className="text-accent no-underline hover:underline"
                      >
                        otwórz sesję →
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
